import {
  Project,
  ProjectCreate,
  ProjectUpdate,
  ProjectField,
  ProjectFieldCreate,
  ProjectFieldUpdate,
  ProjectSettings,
  ProjectSettingsCreate,
  ProjectSettingsUpdate,
  ProjectAlias,
  ProjectAliasCreate,
  ProjectAliasUpdate,
  ProjectColors,
  ProjectColorsCreate,
} from "../types/Project";
import {
  SubmissionsExtendedList,
  SumbissionMassDelete,
} from "../types/Submission";
import { EmailsResponse, SendMailResponse } from "../types/Mails";
import { ProjectsList } from "../types/Project";
import {
  apiGet,
  apiPost,
  apiPut,
  apiDelete,
  apiPatch,
  apiDownload,
} from "./apiClient";

// API functions
export const fetchProjects = async (
  organizationId: string,
): Promise<ProjectsList> => {
  return apiGet<ProjectsList>(`/organizations/${organizationId}/projects`, {
    requiresAuth: true,
  });
};

export const fetchProjectById = async (id: string): Promise<Project> => {
  return apiGet<Project>(`/projects/${id}`, {
    requiresAuth: true,
  });
};

// Public project colors response type
export interface PublicProjectColorsResponse {
  form_background: string;
  text_color: string;
  button_gradient_start: string;
  button_gradient_end: string;
  button_text_color: string;
}

// Public project form response type
export interface PublicProjectFormResponse {
  id: string;
  name: string;
  description: string | null;
  conclusion: string | null;
  logo_url: string | null;
  banner_url: string | null;
  background_url: string | null;
  fields: Array<{
    id: string;
    name: string;
    label: string;
    type: string;
    required: boolean;
    multiple: boolean;
    order: number;
    help_text: string | null;
    description?: string | null;
    confirm?: boolean;
  }>;
  colors: PublicProjectColorsResponse;
}

// Public version to fetch project form without authentication
export const fetchProjectPublic = async (
  id: string,
): Promise<PublicProjectFormResponse> => {
  return apiGet<PublicProjectFormResponse>(`/public/projects/${id}/form`, {
    requiresAuth: false,
  });
};

// Submit form data for a public project (no auth required)
export const submitPublicForm = async (
  projectId: string,
  formData: Record<string, string>,
  videoId?: string,
): Promise<void> => {
  const submission: Record<string, unknown> = {
    infos: Object.entries(formData).map(([fieldId, value]) => ({
      field_id: fieldId,
      value: value,
    })),
  };
  if (videoId) {
    submission.video_id = videoId;
  }
  return apiPost<void>(
    `/public/projects/${projectId}/submissions`,
    submission,
    { requiresAuth: false },
  );
};

// Publish a draft project
export const publishProject = async (projectId: string): Promise<Project> => {
  return apiPost<Project>(`/projects/${projectId}/publish`, {});
};

// Fetch submissions for a project with pagination, sorting, and search
export interface SubmissionQueryParams {
  skip?: number;
  limit?: number;
  sort_field?: string;
  sort_order?: "asc" | "desc";
  search?: string;
  column_filters?: string;
}

export const fetchProjectSubmissions = async (
  projectId: string,
  params: SubmissionQueryParams = {},
): Promise<SubmissionsExtendedList> => {
  const queryParams = new URLSearchParams();

  if (params.skip !== undefined)
    queryParams.set("skip", params.skip.toString());
  if (params.limit !== undefined)
    queryParams.set("limit", params.limit.toString());
  if (params.sort_field) queryParams.set("sort_field", params.sort_field);
  if (params.sort_order) queryParams.set("sort_order", params.sort_order);
  if (params.search) queryParams.set("search", params.search);
  if (params.column_filters)
    queryParams.set("column_filters", params.column_filters);

  const url = `/projects/${projectId}/submissions${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
  return apiGet<SubmissionsExtendedList>(url, { requiresAuth: true });
};

// Delete a submission
export const deleteSubmission = async (submissionId: string): Promise<void> => {
  return apiDelete<void>(`/submissions/${submissionId}`, {
    requiresAuth: true,
  });
};

// Mass delete submissions
export const massDeleteSubmissions = async (
  projectId: string,
  data: SumbissionMassDelete,
): Promise<void> => {
  return apiPost<void>(`/projects/${projectId}/submissions/delete`, data, {
    requiresAuth: true,
  });
};

export const uploadProjectBanner = async (
  id: string,
  bannerFile: File,
): Promise<Project> => {
  const formData = new FormData();
  formData.append("banner", bannerFile);

  return apiPost<Project>(`/projects/${id}/banner`, formData, {
    requiresAuth: true,
  });
};

export const createProject = async (
  organizationId: string,
  project: ProjectCreate,
  logoFile?: File,
  bannerFile?: File,
  backgroundFile?: File,
): Promise<Project> => {
  const formData = new FormData();

  // Add project data (no need for organization_id in form data anymore)
  formData.append("name", project.name);
  if (project.description) formData.append("description", project.description);
  if (project.conclusion) formData.append("conclusion", project.conclusion);
  if (project.email_subject)
    formData.append("email_subject", project.email_subject);
  if (project.email_mjml) formData.append("email_mjml", project.email_mjml);
  if (project.email_template)
    formData.append("email_template", project.email_template);
  if (project.smtp_settings_id)
    formData.append("smtp_settings_id", project.smtp_settings_id);

  // Add files if provided
  if (logoFile instanceof File) formData.append("logo", logoFile);
  if (bannerFile instanceof File) formData.append("banner", bannerFile);
  if (backgroundFile instanceof File)
    formData.append("background", backgroundFile);

  return apiPost<Project>(
    `/organizations/${organizationId}/projects`,
    formData,
    { requiresAuth: true },
  );
};

export const updateProject = async (
  id: string,
  updates: Partial<ProjectUpdate>,
  logoFile?: File,
  bannerFile?: File,
  backgroundFile?: File,
): Promise<Project> => {
  const formData = new FormData();

  // Add only modified project data
  if (updates.name !== undefined) formData.append("name", updates.name);
  if (updates.description !== undefined)
    formData.append("description", updates.description || "");
  if (updates.conclusion !== undefined)
    formData.append("conclusion", updates.conclusion || "");
  if (updates.email_subject !== undefined)
    formData.append("email_subject", updates.email_subject || "");
  if (updates.email_template !== undefined)
    formData.append("email_template", updates.email_template || "");
  if (updates.email_mjml !== undefined)
    formData.append("email_mjml", updates.email_mjml || "");
  if (updates.smtp_settings_id !== undefined)
    formData.append("smtp_settings_id", updates.smtp_settings_id || "");
  // Handle URL fields (including null values for removal)
  if (updates.logo_url !== undefined)
    formData.append("logo_url", updates.logo_url || "");
  if (updates.banner_url !== undefined)
    formData.append("banner_url", updates.banner_url || "");
  if (updates.background_url !== undefined)
    formData.append("background_url", updates.background_url || "");
  // Handle deletion flags
  if ((updates as Partial<ProjectUpdate>).delete_logo === true)
    formData.append("delete_logo", "true");
  if ((updates as Partial<ProjectUpdate>).delete_banner === true)
    formData.append("delete_banner", "true");
  if ((updates as Partial<ProjectUpdate>).delete_background === true)
    formData.append("delete_background", "true");
  // Add files if provided
  if (logoFile) {
    console.log("Adding logo file:", logoFile.name, logoFile.type);
    formData.append("logo", logoFile, logoFile.name);
  }
  if (bannerFile) {
    console.log("Adding banner file:", bannerFile.name, bannerFile.type);
    formData.append("banner", bannerFile, bannerFile.name);
  }
  if (backgroundFile) {
    console.log(
      "Adding background file:",
      backgroundFile.name,
      backgroundFile.type,
    );
    formData.append("background", backgroundFile, backgroundFile.name);
  }

  // Log FormData contents
  console.log("FormData contents:");
  for (const pair of formData.entries()) {
    console.log(pair[0], pair[1]);
  }

  return apiPut<Project>(`/projects/${id}`, formData, { requiresAuth: true });
};

export const deleteProject = async (id: string, name: string): Promise<void> => {
  await apiDelete<void>(`/projects/${id}`, {
    requiresAuth: true,
    body: JSON.stringify({ name }),
  });
};

export const duplicateProject = async (id: string): Promise<Project> => {
  return apiPost<Project>(
    `/projects/${id}/duplicate`,
    {},
    { requiresAuth: true },
  );
};

export const uploadProjectLogo = async (
  id: string,
  logoFile: File,
): Promise<Project> => {
  const formData = new FormData();
  formData.append("logo", logoFile);

  return apiPost<Project>(`/projects/${id}/logo`, formData, {
    requiresAuth: true,
  });
};

export const uploadProjectBackground = async (
  id: string,
  backgroundFile: File,
): Promise<Project> => {
  const formData = new FormData();
  formData.append("background", backgroundFile);

  return apiPost<Project>(`/projects/${id}/background`, formData, {
    requiresAuth: true,
  });
};

export const createProjectField = async (
  id: string,
  field: ProjectFieldCreate,
): Promise<ProjectField> => {
  return apiPost<ProjectField>(`/projects/${id}/fields`, field, {
    requiresAuth: true,
  });
};

export const updateProjectField = async (
  fieldId: string,
  field: ProjectFieldUpdate,
): Promise<ProjectField> => {
  return apiPut<ProjectField>(`/fields/${fieldId}`, field, {
    requiresAuth: true,
  });
};

export const deleteProjectField = async (fieldId: string): Promise<void> => {
  await apiDelete<void>(`/fields/${fieldId}`, { requiresAuth: true });
};

export const updateProjectFieldsOrder = async (
  id: string,
  fieldOrders: Array<{ field_id: string; order: number }>,
): Promise<void> => {
  await apiPatch<void>(`/projects/${id}/fields/order`, fieldOrders, {
    requiresAuth: true,
  });
};

// Project Settings API functions
export const fetchProjectSettings = async (
  projectId: string,
): Promise<ProjectSettings> => {
  return apiGet<ProjectSettings>(`/projects/${projectId}/settings`, {
    requiresAuth: true,
  });
};

export const updateProjectSettings = async (
  projectId: string,
  settings: ProjectSettingsCreate,
): Promise<ProjectSettings> => {
  return apiPut<ProjectSettings>(`/projects/${projectId}/settings`, settings, {
    requiresAuth: true,
  });
};

export const patchProjectSettings = async (
  projectId: string,
  settings: ProjectSettingsUpdate,
): Promise<ProjectSettings> => {
  return apiPut<ProjectSettings>(`/projects/${projectId}/settings`, settings, {
    requiresAuth: true,
  });
};

// Project Aliases API functions

// Fetch project by alias (public) - returns form data for public display
export const fetchProjectByAlias = async (
  alias: string,
): Promise<PublicProjectFormResponse> => {
  return apiGet<PublicProjectFormResponse>(`/public/p/${alias}`, {
    requiresAuth: false,
  });
};

// Alias management
export const fetchProjectAliases = async (
  projectId: string,
): Promise<ProjectAlias[]> => {
  return apiGet<ProjectAlias[]>(`/projects/${projectId}/aliases`, {
    requiresAuth: true,
  });
};

export const createProjectAlias = async (
  projectId: string,
  alias: Omit<ProjectAliasCreate, "project_id">,
): Promise<ProjectAlias> => {
  const aliasWithProjectId = { ...alias, project_id: projectId };
  return apiPost<ProjectAlias>(
    `/projects/${projectId}/aliases`,
    aliasWithProjectId,
    { requiresAuth: true },
  );
};

export const updateProjectAlias = async (
  aliasId: string,
  alias: ProjectAliasUpdate,
): Promise<ProjectAlias> => {
  return apiPut<ProjectAlias>(`/aliases/${aliasId}`, alias, {
    requiresAuth: true,
  });
};

export const deleteProjectAlias = async (aliasId: string): Promise<void> => {
  return apiDelete<void>(`/aliases/${aliasId}`, {
    requiresAuth: true,
  });
};

// Generate QR code URL for display
export const getAliasQRCodeUrl = async (aliasId: string): Promise<string> => {
  return apiDownload(`/aliases/${aliasId}/qrcode`, {
    download: false,
    requiresAuth: true,
  });
};

// Download QR code file
export const downloadAliasQRCode = async (
  aliasId: string,
  alias: string,
): Promise<void> => {
  await apiDownload(`/aliases/${aliasId}/qrcode`, {
    download: true,
    filename: `qrcode-${alias}.png`,
    requiresAuth: true,
  });
};

export const sendProjectMails = async (
  projectId: string,
  submissionIds: string[],
  videoId: string,
): Promise<SendMailResponse> => {
  const emails = submissionIds.map((submissionId) => ({
    email: "",
    infos: {},
    submission_id: submissionId,
    video_id: videoId,
  }));

  return apiPost<SendMailResponse>(
    `/projects/${projectId}/emails`,
    {
      emails,
    },
    { requiresAuth: true },
  );
};

export const fetchProjectEmails = async (
  projectId: string,
  skip = 0,
  limit = 100,
): Promise<EmailsResponse> => {
  const params = new URLSearchParams({
    skip: skip.toString(),
    limit: limit.toString(),
  });
  return apiGet<EmailsResponse>(
    `/projects/${projectId}/emails?${params.toString()}`,
    { requiresAuth: true },
  );
};

export const resetProjectMjml = async (projectId: string): Promise<Project> => {
  return apiPost<Project>(
    `/projects/${projectId}/reset-mjml`,
    {},
    { requiresAuth: true },
  );
};

export const exportSubmissionsCSV = async (
  projectId: string,
  params: SubmissionQueryParams = {},
): Promise<string> => {
  const queryParams = new URLSearchParams();

  if (params.sort_field) queryParams.set("sort_field", params.sort_field);
  if (params.sort_order) queryParams.set("sort_order", params.sort_order);
  if (params.search) queryParams.set("search", params.search);
  if (params.column_filters)
    queryParams.set("column_filters", params.column_filters);

  const url = `/projects/${projectId}/submissions/export/csv${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
  return apiDownload(url, {
    download: true,
    requiresAuth: true,
  });
};

export interface ProjectEngagementStats {
  total_views: number;
  total_downloads: number;
  total_shares: number;
}

export const getProjectEngagementStats = async (
  projectId: string,
): Promise<ProjectEngagementStats> => {
  return apiGet<ProjectEngagementStats>(
    `/projects/${projectId}/engagement-stats`,
    { requiresAuth: true },
  );
};

// Project Colors API functions
export const getProjectColors = async (
  projectId: string,
): Promise<ProjectColors> => {
  return apiGet<ProjectColors>(`/projects/${projectId}/colors`, {
    requiresAuth: true,
  });
};

export const updateProjectColors = async (
  projectId: string,
  colors: ProjectColorsCreate,
): Promise<ProjectColors> => {
  return apiPut<ProjectColors>(`/projects/${projectId}/colors`, colors, {
    requiresAuth: true,
  });
};
